import type { DataAttributeDto } from './datamodel';
import type { Scope } from './sites';
import { z } from 'zod';

/** Archivio (Fase 2): i nomi dei campi seguono quelli degli strumenti MCP dell'archivio. */

export type ArchiveFieldKind = 'testo' | 'numero' | 'data';

export interface ArchiveImport {
  tipo: string;
  nuove: number;
  aggiornate: number;
}

export interface FileAnalysis {
  documento_id: string;
  /** Chiave del file nello storage del tenant (per rileggerlo, es. per far leggere immagini e scansioni al motore). */
  chiave_file?: string;
  formato?: 'pdf' | 'testo' | 'office' | 'binario';
  tipo_file: 'tabella' | 'documento';
  foglio?: string | null;
  colonne?: Array<{ nome: string; tipo: ArchiveFieldKind }>;
  righe_totali?: number;
  anteprima?: Array<Record<string, string | number | null>>;
  firma?: {
    id: string;
    nota: boolean;
    tipo: string | null;
    mappatura: unknown;
    colonna_chiave: string | null;
  };
  importazione?: ArchiveImport | null;
  pagine?: number;
  testo?: string;
  senza_testo?: boolean;
  /** Fattura elettronica letta dal tracciato (ADR 0169): `FatturaPASalvata` di @bk/standards. */
  fattura_pa?: unknown;
  /** Allegati incorporati nella fattura, già conservati: diventano documenti a sé. */
  allegati_fattura?: Array<{ nome: string; chiave_file: string; byte: number }>;
}

export const ConfirmMappingSchema = z.object({
  tipo: z.object({
    nome: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/, 'usare minuscole, cifre e "_"'),
    etichetta: z.string().trim().min(1).max(120),
  }),
  attributi: z
    .array(
      z.object({
        colonna: z.string(),
        attributo: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
        tipo: z.enum(['testo', 'numero', 'data']),
      }),
    )
    .min(1)
    .max(200),
  colonna_chiave: z.string().nullable(),
});
export type ConfirmMappingInput = z.infer<typeof ConfirmMappingSchema>;

export interface ArchiveType {
  nome: string;
  etichetta: string;
  descrizione?: string | null;
  attributi: Array<
    {
      nome: string;
      tipo: ArchiveFieldKind;
      origine?: string;
      etichetta?: string;
    } & Partial<DataAttributeDto>
  >;
  /** Chi è il master: `archive` (file caricati), `modello` (ricavato dal sistema) o la chiave del programma collegato. */
  master: string;
  entita: number;
  rimossi?: number;
  aggiornato_il?: string | null;
}

export const ArchiveValueKindSchema = z.enum(['testo', 'numero', 'data', 'data_ora', 'ora']);

export const ArchiveSearchSchema = z.object({
  tipo: z.string(),
  filtri: z
    .array(
      z.object({
        attributo: z.string(),
        operatore: z.enum([
          'uguale',
          'contiene',
          'include',
          'maggiore',
          'minore',
          'maggiore_uguale',
          'minore_uguale',
        ]),
        valore: z.string(),
        tipo_valore: ArchiveValueKindSchema.optional(),
      }),
    )
    .max(400)
    .default([]),
  testo: z.string().max(200).nullable().default(null),
  limite: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
  /** Anche le righe rimosse dalla fonte. */
  rimossi: z.boolean().default(false),
  /** Solo ciò che riguarda questa sede (più ciò che vale per l'impresa), ADR 0057. */
  sede: z.string().uuid().nullable().default(null),
  /** Con `sede`: solo ciò che è attribuito a quella sede, senza ciò che vale per tutta l'azienda (ADR 0123). */
  solo_sede: z.boolean().default(false),
  /** Solo ciò che è legato a questa attività economica, ADR 0057. */
  attivita: z.string().uuid().nullable().default(null),
  /** Ordinamento tipizzato prima della paginazione; senza attributo, dalle più recenti. */
  ordina_per: z.string().nullable().default(null),
  direzione: z.enum(['asc', 'desc']).default('asc'),
  tipo_ordine: ArchiveValueKindSchema.optional(),
  elenchi: z.array(z.string()).max(200).default([]),
});
export type ArchiveSearchInput = z.infer<typeof ArchiveSearchSchema>;

export interface ArchiveSearchResult {
  opzioni?: Record<string, string[]>;
  righe: Array<{
    id: string;
    attributi: Record<string, unknown>;
    fonte: string;
    riferimento: string | null;
    modalita: string;
    acquisito_il: string;
    versione: number;
    rimosso_il?: string | null;
    legami?: Array<{
      sede: string | null;
      attivita: string | null;
      stato: string;
      deciso_da: string;
      fonte: string;
      motivo: string | null;
    }>;
  }>;
  totale: number;
}

export const ArchiveAggregateSchema = z.object({
  tipo: z.string(),
  raggruppa_per: z.string(),
  metrica: z.enum(['conteggio', 'somma', 'media']),
  attributo: z.string().nullable().default(null),
});

/**
 * Fatti di un tipo dal database (ADR 0251 § 6): conteggio, totale, primo e ultimo, con i permessi di chi chiede. Senza
 * `attributo_data` o `attributo_importo` vale l'unico campo del tipo con quella forma, se ce n'è uno solo.
 */
export const ArchiveFactsSchema = z.object({
  tipo: z.string().min(1).max(63),
  attributo_data: z.string().max(100).nullable().default(null),
  attributo_importo: z.string().max(100).nullable().default(null),
  dal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  al: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  filtri: z
    .array(z.object({ attributo: z.string().max(100), operatore: z.enum(['uguale', 'diverso', 'contiene', 'presente', 'assente', 'maggiore', 'minore', 'maggiore_uguale', 'minore_uguale']), valore: z.string().max(500).default('') }))
    .max(20)
    .default([]),
  soggetto: z.object({ tipo: z.string().min(1).max(63), rif: z.string().min(1).max(200) }).nullable().default(null).describe('Solo le voci legate a questa voce'),
  ordine: z.enum(['data_desc', 'data_asc']).default('data_desc'),
  limite: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
});
export type ArchiveFactsInput = z.infer<typeof ArchiveFactsSchema>;

/** Riempire un'informazione di tipo lista di un processo da un file caricato. */
export const AcquireSchema = z.object({
  info_key: z.string(),
  documento_id: z.string().uuid(),
  /** campo della lista → colonna del file; omesso = usa la procedura già appresa. */
  field_map: z.record(z.string().nullable()).optional(),
});
export type AcquireInput = z.infer<typeof AcquireSchema>;

export type AcquireResult =
  | {
      needs_mapping: true;
      colonne: Array<{ nome: string; tipo: ArchiveFieldKind }>;
      campi: Array<{ nome: string; tipo: ArchiveFieldKind }>;
      proposta: Record<string, string | null>;
      righe_totali: number;
    }
  | {
      needs_mapping: false;
      righe: Array<Record<string, string | number>>;
      field_map: Record<string, string | null>;
      appresa: boolean;
      documento_id: string;
    };

const filterSchema = z.object({
  attributo: z.string(),
  operatore: z.enum(['uguale', 'contiene', 'maggiore', 'minore']),
  valore: z.string(),
});

/** Come un'informazione di un processo si riempie dai dati dell'archivio (procedura di acquisizione). */
export const ArchiveAcquisitionConfigSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('lista'),
    tipo: z.string(),
    filtri: z.array(filterSchema).max(10).default([]),
    field_map: z.record(z.string().nullable()),
  }),
  z.object({
    kind: z.literal('presenze'),
    /** Tipo che contiene le assenze (dal/al) per persona. */
    tipo: z.string(),
    attributo_persona: z.string(),
    confronto: z.enum(['email', 'nome']),
    attributo_dal: z.string(),
    attributo_al: z.string(),
  }),
]);
export type ArchiveAcquisitionConfig = z.infer<typeof ArchiveAcquisitionConfigSchema>;

export const AcquireArchiveSchema = z.object({
  info_key: z.string(),
  config: ArchiveAcquisitionConfigSchema.optional(),
});

export type AcquireArchiveResult =
  | {
      kind: 'lista';
      righe: Array<Record<string, string>>;
      fonte: string;
      config: ArchiveAcquisitionConfig;
    }
  | {
      kind: 'presenze';
      presenze: Array<{ person_id: string; name: string; present: boolean; motivo: string | null }>;
      non_abbinate: string[];
      fonte: string;
      config: ArchiveAcquisitionConfig;
    };

/** Un file dell'archivio dentro una voce dei documenti obbligatori. */
export interface RequiredDocumentFileDto {
  /** Id del documento in archivio. */
  id: string;
  titolo: string;
  data: string | null;
  /** Per le voci per periodo: il periodo a cui si riferisce (null finché non si sa). */
  periodo: string | null;
  periodo_etichetta: string | null;
  /** Per le raccolte: l'elemento a cui si riferisce (null finché non si sa). */
  elemento: string | null;
  elemento_etichetta: string | null;
  collegato_da: 'utente' | 'motore';
}

/** Un periodo di una voce per periodo (es. un esercizio per il bilancio) o un elemento di una raccolta. */
export interface RequiredPeriodDto {
  chiave: string;
  etichetta: string;
  /** Per un elemento di una raccolta: da dove risulta (es. "12 fatture"). */
  fonte?: string | null;
  stato: 'caricato' | 'mancante' | 'non_dovuto';
}

/**
 * Un documento che l'azienda deve avere in archivio (elenco ricavato dal motore per quell'azienda). È una
 * cartella: un documento unico, uno che si rinnova (validita_mesi), uno per periodo (frequenza_mesi) o una
 * raccolta, uno per ogni soggetto, pratica o bene (raccolta).
 */
export interface RequiredDocumentDto {
  id: string;
  titolo: string;
  perche: string;
  /** Il documento preciso da caricare: come si chiama, chi lo rilascia, dove si trova. */
  cosa_caricare: string | null;
  riferimento: string | null;
  /** Se dipende da un fatto non ancora noto: la condizione da verificare. */
  condizione: string | null;
  /** La voce dello strato da cui viene (ADR 0195 § 7): la sezione per le domande del pop-up; null se non viene dagli strati. */
  voce_strato: string | null;
  validita_mesi: number | null;
  /** Per periodo: durata del periodo in mesi (12 annuale, 1 mensile…). */
  frequenza_mesi: number | null;
  /** Per periodo: da quando serve (null se non è noto: si chiede solo l'ultimo periodo). */
  dal: string | null;
  /** Per periodo: quale data del documento ne indica il periodo. */
  periodo_di: string | null;
  /** Raccolta: un documento per ogni soggetto, pratica o bene. */
  raccolta: boolean;
  /**
   * Regole del tipo di documento dalla scheda (ADR 0143): i formati ammessi per la copia d'archivio, detti per la
   * persona, cosa non vale al suo posto, la norma del termine e gli anni di conservazione; null senza tipo.
   */
  regole: {
    formati: string;
    non_valgono: string[];
    /** La norma del termine (o della validità); null se nessuna regola è verificata. */
    norma: string | null;
    conservazione_anni: number | null;
    /** Regole della scheda senza una norma verificata (ADR 0144): non si applicano. */
    da_verificare: Array<'tenuta' | 'termine' | 'validita' | 'conservazione'>;
  } | null;
  /**
   * non_ancora: per periodo, nessun periodo è ancora dovuto. da_confermare: raccolta con i file degli elementi
   * noti, ma solo la persona può dire se sono tutti.
   */
  stato:
    | 'da_caricare'
    | 'incompleto'
    | 'da_confermare'
    | 'caricato'
    | 'da_rinnovare'
    | 'non_ancora'
    | 'non_riguarda';
  file: RequiredDocumentFileDto[];
  /** Per periodo: i periodi dovuti fino a oggi (e quelli già caricati), dal più vecchio. */
  periodi: RequiredPeriodDto[] | null;
  /** Raccolta: gli elementi noti (dai fatti o dai file). */
  elementi: RequiredPeriodDto[] | null;
  /** Raccolta: la persona ha confermato che sono tutti. */
  confermato: boolean;
  /** Periodi o elementi dovuti senza file. */
  mancanti: number;
  /** Data oltre la quale il documento più recente va rinnovato. */
  scade_il: string | null;
  /** Dell'azienda come soggetto o di una sede (ADR 0017). */
  ambito: Scope;
}

export interface RequiredDocumentsState {
  data: RequiredDocumentDto[];
  generating: boolean;
  generated_at: string | null;
  error: string | null;
  /** Serve sapere qualcosa dell'azienda (sezione Azienda) prima di generare l'elenco. */
  needs_profile: boolean;
}

export const RequiredDocumentStatusSchema = z.object({ stato: z.enum(['attivo', 'non_riguarda']) });

const PeriodKey = z.string().regex(/^\d{4}(-\d{2})?$/);
/** Un periodo segnato come non dovuto (es. un mese senza versamenti), o di nuovo dovuto. */
export const RequiredPeriodSchema = z.object({ periodo: PeriodKey, non_dovuto: z.boolean() });
/** Il periodo a cui si riferisce un file della voce. */
export const RequiredFilePeriodSchema = z.object({ periodo: PeriodKey.nullable() });
const ItemKey = z.string().min(1).max(120);
export const RequiredUploadQuerySchema = z.object({
  periodo: PeriodKey.optional(),
  elemento: ItemKey.optional(),
});
/** Un documento già in archivio che entra nella cartella della voce (nel periodo o nell'elemento, se indicati). ADR 0122. */
export const RequiredLinkSchema = RequiredUploadQuerySchema;
/** Un elemento di una raccolta che non serve (es. un consulente senza contratto scritto), o di nuovo dovuto. */
export const RequiredItemSchema = z.object({ elemento: ItemKey, non_dovuto: z.boolean() });
/** L'elemento a cui si riferisce un file della raccolta. */
export const RequiredFileItemSchema = z.object({ elemento: ItemKey.nullable() });
/** La persona conferma (o no) che i file della raccolta sono tutti. */
export const RequiredConfirmSchema = z.object({ completo: z.boolean() });
